import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  UserPlusIcon,
  EnvelopeIcon,
  PhoneIcon,
  AcademicCapIcon,
  CalendarIcon,
  MapPinIcon,
  UserIcon,
  IdentificationIcon,
  HeartIcon,
  XMarkIcon,
  CheckCircleIcon,
  GlobeAltIcon,
  HomeIcon,
  BookOpenIcon,
  UserGroupIcon,
  BriefcaseIcon,
} from "@heroicons/react/24/outline";
import { useAuth } from "../context/AuthContext";
import apiClient from "../api/client";
import { FireLoader } from "../components/Loader";
import {
  showSuccess,
  showError,
  showWarning,
  showInfo,
  showConfirm,
} from "../utils/alert";

// Sierra Leone provinces (including the Western Area)
const SIERRA_LEONE_PROVINCES = [
  "Eastern Province",
  "Northern Province",
  "Southern Province",
  "Western Area",
];

export default function AddStudent() {
  const navigate = useNavigate();
  const {
    user,
    hasFeature,
    getPlanLimit,
    planAccess,
    planAccessLoading,
  } = useAuth();
  const [currentStep, setCurrentStep] = useState(1);
  const [errors, setErrors] = useState({});
  const [classes, setClasses] = useState([]);
  const [academicYears, setAcademicYears] = useState([]);
  const [isMobile, setIsMobile] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [studentCount, setStudentCount] = useState(0);
  const [usageLoading, setUsageLoading] = useState(true);

  const studentLimit = getPlanLimit("students");
  const hasUnlimitedStudents = planAccess?.limits?.unlimited_students === true;
  const canCreateStudents = hasFeature("students");
  const studentLimitReached =
    !hasUnlimitedStudents &&
    studentLimit !== null &&
    studentCount >= studentLimit;

  useEffect(() => {
    const checkMobile = () => {
      const userAgent = navigator.userAgent || navigator.vendor || window.opera;
      const mobileRegex =
        /android|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini/i;
      setIsMobile(mobileRegex.test(userAgent.toLowerCase()));
    };
    checkMobile();
  }, []);

  const [studentData, setStudentData] = useState({
    firstName: "",
    lastName: "",
    middleName: "",
    dateOfBirth: "",
    gender: "",
    nationality: "Sierra Leonean",
    religion: "",
    tribe: "",
    address: "",
    city: "",
    state: "",
    country: "Sierra Leone",
    phone: "",
    currentClassId: "",
    academicYearId: "",
    enrollmentDate: new Date().toISOString().split("T")[0],
    medicalConditions: "",
    allergies: "",
  });

  const [parentData, setParentData] = useState({
    parentFirstName: "",
    parentLastName: "",
    parentRelationship: "",
    parentEmail: "",
    parentPhone: "",
    parentAddress: "",
    parentOccupation: "",
    emergencyName: "",
    emergencyRelationship: "",
    emergencyPhone: "",
    emergencyAddress: "",
    hasSecondaryParent: false,
    secondaryParentFirstName: "",
    secondaryParentLastName: "",
    secondaryParentRelationship: "",
    secondaryParentEmail: "",
    secondaryParentPhone: "",
    secondaryParentAddress: "",
  });

  useEffect(() => {
    fetchClasses();
    fetchAcademicYears();
  }, []);

  useEffect(() => {
    let active = true;

    const loadStudentUsage = async () => {
      try {
        setUsageLoading(true);
        const response = await apiClient.get("/students", {
          params: { page: 1, limit: 1 },
        });
        const payload = response.data || {};
        const total = Number(
          payload.pagination?.total ??
          payload.total ??
          payload.count ??
          payload.data?.total ??
          payload.data?.count ??
          payload.data?.pagination?.total ??
          (Array.isArray(payload.data) ? payload.data.length : 0),
        );
        if (active) setStudentCount(Number.isFinite(total) ? total : 0);
      } catch (error) {
        console.error("Failed to load student plan usage:", error);
      } finally {
        if (active) setUsageLoading(false);
      }
    };

    if (canCreateStudents) loadStudentUsage();
    else setUsageLoading(false);

    return () => {
      active = false;
    };
  }, [canCreateStudents]);

  const fetchClasses = async () => {
    try {
      const response = await apiClient.get("/classes", {
        params: { limit: 100, status: "active" },
      });
      const classesData = response.data?.data || response.data || [];
      setClasses(classesData);
    } catch (error) {
      console.error("Error fetching classes:", error);
      await showError({
        text: "Failed to load classes. Please refresh the page.",
      });
    }
  };

  const fetchAcademicYears = async () => {
    try {
      const response = await apiClient.get("/academic-years");
      const yearsData = response.data?.data || response.data || [];
      setAcademicYears(yearsData);
      const current = yearsData.find((y) => y.is_current);
      if (current)
        setStudentData((prev) => ({ ...prev, academicYearId: current.id }));
    } catch (error) {
      console.error("Error fetching academic years:", error);
      await showError({ text: "Failed to load academic years." });
    }
  };

  const formatClassDisplay = (cls) => {
    if (!cls) return "";
    let display = cls.name ? cls.name.toUpperCase() : "";
    if (cls.faculty && cls.faculty !== "general") {
      display += ` (${cls.faculty.toUpperCase()})`;
    }
    return display;
  };

  const handleStudentChange = (e) => {
    const { name, value } = e.target;
    setStudentData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const handleParentChange = (e) => {
    const { name, value } = e.target;
    setParentData((prev) => {
      const newData = { ...prev, [name]: value };
      if (name === "parentFirstName" || name === "parentLastName") {
        const first = name === "parentFirstName" ? value : prev.parentFirstName;
        const last = name === "parentLastName" ? value : prev.parentLastName;
        newData.emergencyName = `${first} ${last}`.trim();
      }
      if (name === "parentPhone") newData.emergencyPhone = value;
      if (name === "parentAddress") newData.emergencyAddress = value;
      if (name === "parentRelationship") newData.emergencyRelationship = value;
      return newData;
    });
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const handleEmergencyChange = (e) => {
    const { name, value } = e.target;
    setParentData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const validateEmail = (email) =>
    /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());

  const validateStep = (step) => {
    const newErrors = {};

    if (step === 1) {
      if (!studentData.firstName.trim())
        newErrors.firstName = "First name is required";
      if (!studentData.lastName.trim())
        newErrors.lastName = "Last name is required";
      if (!studentData.dateOfBirth)
        newErrors.dateOfBirth = "Date of birth is required";
      if (!studentData.gender) newErrors.gender = "Gender is required";
      if (!studentData.address.trim())
        newErrors.address = "Address is required";
    }

    if (step === 2) {
      if (!studentData.currentClassId)
        newErrors.currentClassId = "Class is required";
      if (!studentData.enrollmentDate)
        newErrors.enrollmentDate = "Enrollment date is required";
    }

    if (step === 4) {
      if (!parentData.parentFirstName.trim())
        newErrors.parentFirstName = "First name is required";
      if (!parentData.parentLastName.trim())
        newErrors.parentLastName = "Last name is required";
      if (!parentData.parentRelationship.trim())
        newErrors.parentRelationship = "Relationship is required";
      if (!parentData.parentPhone.trim())
        newErrors.parentPhone = "Phone number is required";
      if (!parentData.parentAddress.trim())
        newErrors.parentAddress = "Address is required";
      if (!parentData.parentOccupation.trim())
        newErrors.parentOccupation = "Occupation is required";
      if (
        !parentData.parentEmail.trim() ||
        !validateEmail(parentData.parentEmail)
      )
        newErrors.parentEmail = "Valid email is required";
    }

    if (step === 5) {
      if (!parentData.emergencyName.trim())
        newErrors.emergencyName = "Name is required";
      if (!parentData.emergencyRelationship.trim())
        newErrors.emergencyRelationship = "Relationship is required";
      if (!parentData.emergencyPhone.trim())
        newErrors.emergencyPhone = "Phone number is required";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const nextStep = () => {
    if (validateStep(currentStep)) {
      setCurrentStep(currentStep + 1);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } else {
      showWarning({
        title: "Validation Error",
        text: "Please fix the highlighted fields before proceeding.",
      });
    }
  };

  const prevStep = () => {
    setCurrentStep(currentStep - 1);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!canCreateStudents) {
      return showError({
        title: "Plan Upgrade Required",
        text: "Student management is not included in your current plan.",
      });
    }

    if (studentLimitReached) {
      return showError({
        title: "Student Limit Reached",
        text: `Your plan allows up to ${studentLimit} students. Upgrade your plan to enrol another student.`,
      });
    }

    if (!validateStep(5)) return;

    setIsSubmitting(true);

    try {
      const payload = {
        first_name: studentData.firstName.trim(),
        last_name: studentData.lastName.trim(),
        middle_name: studentData.middleName?.trim() || null,
        date_of_birth: studentData.dateOfBirth,
        gender: studentData.gender.toLowerCase(),
        nationality: studentData.nationality,
        religion: studentData.religion?.trim() || null,
        tribe: studentData.tribe?.trim() || null,
        address: studentData.address.trim(),
        city: studentData.city?.trim() || null,
        state: studentData.state?.trim() || null,
        country: studentData.country,
        phone: studentData.phone.trim() || null,
        current_class_id: parseInt(studentData.currentClassId),
        academic_year_id: parseInt(studentData.academicYearId),
        enrollment_date: studentData.enrollmentDate,
        medical_conditions: studentData.medicalConditions?.trim() || null,
        allergies: studentData.allergies?.trim() || null,
        parent_first_name: parentData.parentFirstName.trim(),
        parent_last_name: parentData.parentLastName.trim(),
        parent_relationship: parentData.parentRelationship.trim(),
        parent_email: parentData.parentEmail.trim(),
        parent_phone: parentData.parentPhone.trim(),
        parent_address: parentData.parentAddress.trim(),
        parent_occupation: parentData.parentOccupation?.trim() || null,
        emergency_contact_name: parentData.emergencyName.trim(),
        emergency_contact_phone: parentData.emergencyPhone.trim(),
        emergency_contact_relationship: parentData.emergencyRelationship.trim(),
        emergency_contact_address: parentData.emergencyAddress.trim(),
      };

      if (parentData.hasSecondaryParent) {
        payload.secondary_parent_first_name =
          parentData.secondaryParentFirstName.trim();
        payload.secondary_parent_last_name =
          parentData.secondaryParentLastName.trim();
        payload.secondary_parent_relationship =
          parentData.secondaryParentRelationship.trim();
        payload.secondary_parent_email = parentData.secondaryParentEmail.trim();
        payload.secondary_parent_phone = parentData.secondaryParentPhone.trim();
        payload.secondary_parent_address =
          parentData.secondaryParentAddress.trim();
      }

      const response = await apiClient.post("/students", payload);
      const linkedExistingParent =
        response.data?.data?.parentUser?.isExisting === true;

      await showSuccess({
        text: linkedExistingParent
          ? "Student enrolled and linked to the parent's existing account."
          : response.data?.message || "Student enrolled successfully! Redirecting...",
      });
      setTimeout(() => navigate("/app/students"), 1500);
    } catch (error) {
      console.error(error);
      const msg = error.response?.data?.message || "Failed to enroll student";
      await showError({ text: msg });
    } finally {
      setIsSubmitting(false);
    }
  };

  const getInputClassName = (fieldName) => {
    const base =
      "w-full pl-11 pr-4 py-3 bg-white border border-gray-200 rounded-lg focus:outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-100 transition-all text-base";
    return errors[fieldName] ? `${base} border-red-300 bg-red-50` : base;
  };

  const renderStepIndicator = () => {
    const steps = [
      { number: 1, label: "Student Info" },
      { number: 2, label: "Academic" },
      { number: 3, label: "Medical" },
      { number: 4, label: "Parent/Guardian" },
      { number: 5, label: "Emergency Contact" },
    ];

    return (
      <div className="mb-8 overflow-x-auto pb-2">
        <div className="flex items-center min-w-max">
          {steps.map((step, index) => (
            <React.Fragment key={step.number}>
              <div className="flex flex-col items-center flex-1 min-w-[70px]">
                <div
                  className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold transition-all ${
                    currentStep >= step.number
                      ? "bg-primary-600 text-white"
                      : "bg-gray-100 text-gray-400"
                  }`}
                >
                  {currentStep > step.number ? (
                    <CheckCircleIcon className="w-4 h-4" />
                  ) : (
                    step.number
                  )}
                </div>
                <span
                  className={`text-xs mt-1.5 text-center ${currentStep >= step.number ? "text-primary-600" : "text-gray-400"}`}
                >
                  {step.label}
                </span>
              </div>
              {index < steps.length - 1 && (
                <div
                  className={`flex-1 h-0.5 mx-2 ${currentStep > step.number ? "bg-primary-600" : "bg-gray-200"}`}
                />
              )}
            </React.Fragment>
          ))}
        </div>
      </div>
    );
  };

  if (isSubmitting)
    return (
      <FireLoader fullScreen={true} size="lg" text="ENROLLING STUDENT..." />
    );

  if (planAccessLoading || usageLoading) {
    return <FireLoader fullScreen={true} size="lg" text="CHECKING PLAN ACCESS..." />;
  }

  if (!canCreateStudents || studentLimitReached) {
    return (
      <div className="min-h-[70vh] flex items-center justify-center px-4">
        <div className="w-full max-w-lg rounded-2xl border border-amber-200 bg-white p-6 text-center shadow-sm sm:p-8">
          <UserPlusIcon className="mx-auto h-12 w-12 text-amber-500" />
          <h1 className="mt-4 text-xl font-bold text-gray-900">
            {!canCreateStudents ? "Student Management Is Locked" : "Student Limit Reached"}
          </h1>
          <p className="mt-2 text-sm leading-6 text-gray-600">
            {!canCreateStudents
              ? "Your current school plan does not allow new student enrolment."
              : `Your school has ${studentCount} of ${studentLimit} students allowed by the current plan.`}
          </p>
          <div className="mt-6 flex flex-col justify-center gap-3 sm:flex-row">
            <button type="button" onClick={() => navigate("/app/students")} className="rounded-lg border border-gray-200 px-4 py-2.5 text-sm font-semibold text-gray-700 hover:bg-gray-50">
              Back to Students
            </button>
            <button type="button" onClick={() => navigate("/app/pricing?feature=students")} className="rounded-lg bg-primary-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-primary-700">
              View Upgrade Plans
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-6 md:py-10 px-4 sm:px-6 lg:px-8">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6 gap-4">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-primary-700 flex items-center gap-3">
              <UserPlusIcon className="w-7 h-7 sm:w-8 sm:h-8 text-primary-600" />
              Enroll New Student
            </h1>
            <p className="text-sm text-gray-500 mt-0.5">
              Complete all steps to register a new student
            </p>
          </div>
          <button
            onClick={() => navigate("/app/students")}
            className="flex items-center gap-2 px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg transition self-start sm:self-auto text-sm"
          >
            <XMarkIcon className="w-4 h-4" /> Cancel
          </button>
        </div>

        {/* Form Card – flat */}
        <div className="bg-white rounded-lg border border-gray-100 p-5 sm:p-8">
          {renderStepIndicator()}

          <form onSubmit={handleSubmit} className="space-y-8">
            {/* STEP 1 */}
            {currentStep === 1 && (
              <div className="space-y-6">
                <h3 className="text-lg font-semibold text-primary-700">
                  Personal Information
                </h3>

                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  {/* First Name */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      First Name <span className="text-red-500">*</span>
                    </label>
                    <div className="relative">
                      <UserIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                      <input
                        type="text"
                        name="firstName"
                        value={studentData.firstName}
                        onChange={handleStudentChange}
                        className={getInputClassName("firstName")}
                        placeholder="John"
                      />
                    </div>
                  </div>
                  {/* Last Name */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Last Name <span className="text-red-500">*</span>
                    </label>
                    <div className="relative">
                      <UserIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                      <input
                        type="text"
                        name="lastName"
                        value={studentData.lastName}
                        onChange={handleStudentChange}
                        className={getInputClassName("lastName")}
                        placeholder="Doe"
                      />
                    </div>
                  </div>
                  {/* Middle Name */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Middle Name
                    </label>
                    <div className="relative">
                      <UserIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                      <input
                        type="text"
                        name="middleName"
                        value={studentData.middleName}
                        onChange={handleStudentChange}
                        className={getInputClassName("middleName")}
                        placeholder="Optional"
                      />
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-1 gap-4">
                  {/* Date of Birth */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Date of Birth <span className="text-red-500">*</span>
                    </label>
                    <div className="relative">
                      <CalendarIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                      <input
                        type="date"
                        name="dateOfBirth"
                        value={studentData.dateOfBirth}
                        onChange={handleStudentChange}
                        className={getInputClassName("dateOfBirth")}
                        style={{ minWidth: "0" }}
                      />
                    </div>
                  </div>
                  {/* Gender */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Gender <span className="text-red-500">*</span>
                    </label>
                    <div className="relative">
                      <UserIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                      <select
                        name="gender"
                        value={studentData.gender}
                        onChange={handleStudentChange}
                        className={`${getInputClassName("gender")} pl-9`}
                      >
                        <option value="">Select gender</option>
                        <option value="male">Male</option>
                        <option value="female">Female</option>
                        <option value="other">Other</option>
                      </select>
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {/* Nationality */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Nationality
                    </label>
                    <div className="relative">
                      <GlobeAltIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                      <input
                        type="text"
                        name="nationality"
                        value={studentData.nationality}
                        onChange={handleStudentChange}
                        className={getInputClassName("nationality")}
                      />
                    </div>
                  </div>
                  {/* Religion */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Religion
                    </label>
                    <div className="relative">
                      <HeartIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                      <select
                        name="religion"
                        value={studentData.religion}
                        onChange={handleStudentChange}
                        className={`${getInputClassName("religion")} pl-9`}
                      >
                        <option value="">Select religion</option>
                        <option value="Christianity">CHRISTIANITY</option>
                        <option value="Islam">ISLAM</option>
                        <option value="Other">OTHER</option>
                      </select>
                    </div>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Address <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <MapPinIcon className="absolute left-3 top-3 w-4 h-4 text-gray-400" />
                    <textarea
                      name="address"
                      value={studentData.address}
                      onChange={handleStudentChange}
                      rows="3"
                      className={`${getInputClassName("address")} pl-9`}
                      placeholder="Full residential address"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  {/* City */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      City
                    </label>
                    <div className="relative">
                      <MapPinIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                      <input
                        type="text"
                        name="city"
                        value={studentData.city}
                        onChange={handleStudentChange}
                        className={getInputClassName("city")}
                      />
                    </div>
                  </div>
                  {/* Province – dropdown with Sierra Leone provinces */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Province
                    </label>
                    <div className="relative">
                      <MapPinIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                      <select
                        name="state"
                        value={studentData.state}
                        onChange={handleStudentChange}
                        className={`${getInputClassName("state")} pl-9`}
                      >
                        <option value="">Select Province</option>
                        {SIERRA_LEONE_PROVINCES.map((province) => (
                          <option key={province} value={province}>
                            {province}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                  {/* Country */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Country
                    </label>
                    <div className="relative">
                      <GlobeAltIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                      <input
                        type="text"
                        name="country"
                        value={studentData.country}
                        onChange={handleStudentChange}
                        className={getInputClassName("country")}
                      />
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {/* Phone */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Phone Number <span className="text-gray-400">(Optional)</span>
                    </label>
                    <div className="relative">
                      <PhoneIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                      <input
                        type="tel"
                        name="phone"
                        value={studentData.phone}
                        onChange={handleStudentChange}
                        className={getInputClassName("phone")}
                        placeholder="+232 76 123 456"
                      />
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* STEP 2 */}
            {currentStep === 2 && (
              <div className="space-y-6">
                <h3 className="text-lg font-semibold text-primary-700">
                  Academic Information
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {/* Class */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Class <span className="text-red-500">*</span>
                    </label>
                    <div className="relative">
                      <BookOpenIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                      <select
                        name="currentClassId"
                        value={studentData.currentClassId}
                        onChange={handleStudentChange}
                        className={`${getInputClassName("currentClassId")} pl-9`}
                      >
                        <option value="">Select Class</option>
                        {classes.map((cls) => (
                          <option key={cls.id} value={cls.id}>
                            {formatClassDisplay(cls)}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                  {/* Academic Year */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Academic Year
                    </label>
                    <div className="relative">
                      <CalendarIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                      <select
                        name="academicYearId"
                        value={studentData.academicYearId}
                        onChange={handleStudentChange}
                        className={`${getInputClassName("academicYearId")} pl-9`}
                      >
                        <option value="">Select Academic Year</option>
                        {academicYears.map((year) => (
                          <option key={year.id} value={year.id}>
                            {year.name}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Enrollment Date <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <CalendarIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <input
                      type="date"
                      name="enrollmentDate"
                      value={studentData.enrollmentDate}
                      onChange={handleStudentChange}
                      className={getInputClassName("enrollmentDate")}
                    />
                  </div>
                </div>
              </div>
            )}

            {/* STEP 3 */}
            {currentStep === 3 && (
              <div className="space-y-6">
                <h3 className="text-lg font-semibold text-primary-700">
                  Medical Information
                </h3>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Medical Conditions
                  </label>
                  <textarea
                    name="medicalConditions"
                    value={studentData.medicalConditions}
                    onChange={handleStudentChange}
                    rows="4"
                    className={getInputClassName("medicalConditions")}
                    placeholder="List any medical conditions..."
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Allergies
                  </label>
                  <textarea
                    name="allergies"
                    value={studentData.allergies}
                    onChange={handleStudentChange}
                    rows="4"
                    className={getInputClassName("allergies")}
                    placeholder="List any allergies..."
                  />
                </div>
              </div>
            )}

            {/* STEP 4 */}
            {currentStep === 4 && (
              <div className="space-y-6">
                <h3 className="text-lg font-semibold text-primary-700">
                  Primary Parent / Guardian
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {/* Parent First Name */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      First Name <span className="text-red-500">*</span>
                    </label>
                    <div className="relative">
                      <UserIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                      <input
                        type="text"
                        name="parentFirstName"
                        value={parentData.parentFirstName}
                        onChange={handleParentChange}
                        className={getInputClassName("parentFirstName")}
                        placeholder="First Name"
                      />
                    </div>
                  </div>
                  {/* Parent Last Name */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Last Name <span className="text-red-500">*</span>
                    </label>
                    <div className="relative">
                      <UserIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                      <input
                        type="text"
                        name="parentLastName"
                        value={parentData.parentLastName}
                        onChange={handleParentChange}
                        className={getInputClassName("parentLastName")}
                        placeholder="Last Name"
                      />
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {/* Parent Relationship */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Relationship <span className="text-red-500">*</span>
                    </label>
                    <div className="relative">
                      <UserGroupIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                      <select
                        name="parentRelationship"
                        value={parentData.parentRelationship}
                        onChange={handleParentChange}
                        className={`${getInputClassName("parentRelationship")} pl-9`}
                      >
                        <option value="">Select relationship</option>
                        <option value="father">Father</option>
                        <option value="mother">Mother</option>
                        <option value="guardian">Guardian</option>
                        <option value="grandparent">Grandparent</option>
                        <option value="uncle">Uncle</option>
                        <option value="aunt">Aunt</option>
                        <option value="sibling">Sibling</option>
                        <option value="other">Other</option>
                      </select>
                    </div>
                  </div>
                  {/* Parent Phone */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Phone <span className="text-red-500">*</span>
                    </label>
                    <div className="relative">
                      <PhoneIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                      <input
                        type="tel"
                        name="parentPhone"
                        value={parentData.parentPhone}
                        onChange={handleParentChange}
                        className={getInputClassName("parentPhone")}
                        placeholder="+232 76 123 456"
                      />
                    </div>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Address <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <HomeIcon className="absolute left-3 top-3 w-4 h-4 text-gray-400" />
                    <textarea
                      name="parentAddress"
                      value={parentData.parentAddress}
                      onChange={handleParentChange}
                      rows="3"
                      className={`${getInputClassName("parentAddress")} pl-9`}
                      placeholder="Parent full address"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Email <span className="text-red-500">*</span>
                    </label>
                    <div className="relative">
                      <EnvelopeIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                      <input
                        type="email"
                        name="parentEmail"
                        value={parentData.parentEmail}
                        onChange={handleParentChange}
                        className={getInputClassName("parentEmail")}
                        placeholder="parent@example.com"
                      />
                    </div>
                    <p className="mt-1.5 text-xs text-gray-500">
                      If this parent already has another child in the school, use the same email to link both children to one parent account.
                    </p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Occupation <span className="text-red-500">*</span>
                    </label>
                    <div className="relative">
                      <BriefcaseIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                      <input
                        type="text"
                        name="parentOccupation"
                        value={parentData.parentOccupation}
                        onChange={handleParentChange}
                        className={getInputClassName("parentOccupation")}
                        placeholder="e.g. Teacher, Trader, Nurse"
                      />
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* STEP 5 */}
            {currentStep === 5 && (
              <div className="space-y-6">
                <h3 className="text-lg font-semibold text-primary-700">
                  Emergency Contact
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Full Name <span className="text-red-500">*</span>
                    </label>
                    <div className="relative">
                      <UserIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                      <input
                        type="text"
                        name="emergencyName"
                        value={parentData.emergencyName}
                        onChange={handleEmergencyChange}
                        className={getInputClassName("emergencyName")}
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Relationship <span className="text-red-500">*</span>
                    </label>
                    <div className="relative">
                      <UserGroupIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                      <select
                        name="emergencyRelationship"
                        value={parentData.emergencyRelationship}
                        onChange={handleEmergencyChange}
                        className={`${getInputClassName("emergencyRelationship")} pl-9`}
                      >
                        <option value="">Select relationship</option>
                        <option value="parent">Parent</option>
                        <option value="mother">Mother</option>
                        <option value="father">Father</option>
                        <option value="guardian">Guardian</option>
                        <option value="sibling">Sibling</option>
                        <option value="grandparent">Grandparent</option>
                        <option value="aunt">Aunt</option>
                        <option value="uncle">Uncle</option>
                        <option value="cousin">Cousin</option>
                        <option value="friend">Friend</option>
                        <option value="neighbor">Neighbor</option>
                        <option value="other">Other</option>
                      </select>
                    </div>
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Phone Number <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <PhoneIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <input
                      type="tel"
                      name="emergencyPhone"
                      value={parentData.emergencyPhone}
                      onChange={handleEmergencyChange}
                      className={getInputClassName("emergencyPhone")}
                    />
                  </div>
                </div>
              </div>
            )}

            {/* Navigation Buttons */}
            <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-between pt-6 border-t border-gray-100">
              {currentStep > 1 && (
                <button
                  type="button"
                  onClick={prevStep}
                  className="px-6 py-2.5 border border-gray-200 rounded-lg hover:bg-gray-50 transition w-full sm:w-auto text-sm"
                >
                  Previous
                </button>
              )}
              {currentStep < 5 ? (
                <button
                  type="button"
                  onClick={nextStep}
                  className="px-8 py-2.5 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition w-full sm:w-auto text-sm"
                >
                  Next Step
                </button>
              ) : (
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="px-8 py-2.5 bg-green-600 text-white rounded-lg hover:bg-green-700 transition disabled:opacity-70 w-full sm:w-auto text-sm"
                >
                  {isSubmitting ? "Enrolling..." : "Enroll Student"}
                </button>
              )}
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}